import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Upload, Download, FileSpreadsheet, CheckCircle, AlertCircle, Info, Clock, Trash2, ArrowLeft, ClipboardPaste, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { useMMDLocations } from '../../hooks/useMMDLocations';
import { useAuth } from '../../context/AuthContext';
import Pagination from '../../components/common/Pagination';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const grades = ['MEO Class 1', 'MEO Class 2', 'MEO Class 4', 'ETO', 'Master', 'Chief Mate', '2MFG'];

const BulkImportQuestionsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Sub-admins can view uploaded batches + download templates + review
  // staged rows, but cannot upload. This flag drives the disabled-with-
  // overlay UX on Step 2, per the trust boundary discussed in tech debt.
  const isSubAdmin = user?.role === 'sub_admin';
  const [functions, setFunctions] = useState([]);
  const [selectedGrade, setSelectedGrade] = useState('');
  const [selectedMMD, setSelectedMMD] = useState('');
  const [selectedFunction, setSelectedFunction] = useState('');
  const [file, setFile] = useState(null);
  // Excel-paste flow: admin pastes TSV (tab-separated, Excel clipboard format)
  // OR CSV into a textarea. We convert it to a CSV Blob and reuse the existing
  // /upload endpoint — no separate backend route needed.
  const [pasteText, setPasteText] = useState('');
  const [showPaste, setShowPaste] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [allFunctions, setAllFunctions] = useState([]);
  const [importHistory, setImportHistory] = useState([]);
  const [stagingCount, setStagingCount] = useState(0);
  
  // Pagination states for Import History
  const [historyCurrentPage, setHistoryCurrentPage] = useState(1);
  const [historyItemsPerPage, setHistoryItemsPerPage] = useState(10);
  
  // Fetch MMD locations from API
  const { locations: mmdLocations } = useMMDLocations();

  useEffect(() => {
    fetchFunctions();
    fetchImportHistory();
    fetchStagingCount();
  }, []);

  // Filter functions by selected grade (oral functions only - this module is for oral questions)
  useEffect(() => {
    if (selectedGrade) {
      const filtered = allFunctions.filter(f => f.grade === selectedGrade && f.exam_type !== 'written');
      setFunctions(filtered);
    } else {
      setFunctions([]);
    }
  }, [selectedGrade, allFunctions]);

  // Reset function selection when grade changes
  useEffect(() => {
    setSelectedFunction('');
  }, [selectedGrade]);

  const fetchFunctions = async () => {
    try {
      const response = await axios.get(`${API}/functions`);
      setAllFunctions(response.data);
    } catch (error) {
      console.error('Failed to fetch functions:', error);
      toast.error('Failed to load functions');
    }
  };

  const fetchImportHistory = async () => {
    try {
      const response = await axios.get(`${API}/admin/bulk-import/history`);
      setImportHistory(response.data);
    } catch (error) {
      console.error('Failed to fetch import history:', error);
    }
  };

  const fetchStagingCount = async () => {
    try {
      const response = await axios.get(`${API}/admin/bulk-import/staging`);
      setStagingCount(response.data.total_count || 0);
    } catch (error) {
      console.error('Failed to fetch staging count:', error);
    }
  };

  const handleDownloadTemplate = async () => {
    if (!selectedGrade || !selectedMMD || !selectedFunction) {
      toast.error('Please select Grade, MMD Location, and Function first');
      return;
    }

    setDownloadingTemplate(true);
    try {
      const response = await axios.post(
        `${API}/admin/bulk-import/generate-template`,
        {
          grade: selectedGrade,
          mmd_location: selectedMMD,
          function_id: selectedFunction
        },
        { responseType: 'blob' }
      );

      // Get filename from content-disposition header or generate one
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'BulkImportTemplate.csv';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) {
          filename = match[1].replace(/"/g, '');
        }
      }

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Template downloaded successfully!');
    } catch (error) {
      console.error('Failed to download template:', error);
      toast.error('Failed to download template');
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const lower = selectedFile.name.toLowerCase();
      const isCsv = lower.endsWith('.csv');
      const isXlsx = lower.endsWith('.xlsx') || lower.endsWith('.xls');
      if (!isCsv && !isXlsx) {
        toast.error('Please select a .csv file (.xlsx is still accepted as legacy)');
        return;
      }
      setFile(selectedFile);
    }
  };

  // Convert TSV/CSV clipboard text → CSV File and run the existing upload flow.
  // Excel & Sheets put tab-separated data on the clipboard by default; CSV pastes
  // also work since the parser handles both delimiters (we sniff via the first line).
  const handlePasteUpload = async () => {
    const text = (pasteText || '').trim();
    if (!text) {
      toast.error('Paste your spreadsheet content first');
      return;
    }
    if (!selectedGrade || !selectedMMD || !selectedFunction) {
      toast.error('Please select Grade, MMD Location, and Function first');
      return;
    }
    // Detect delimiter from header row: prefer tab (Excel), fallback to comma
    const firstLine = text.split(/\r?\n/, 1)[0] || '';
    const delim = firstLine.includes('\t') ? '\t' : ',';
    let csv = text;
    if (delim === '\t') {
      // Convert TSV → CSV (RFC 4180-safe quoting)
      const rows = text.split(/\r?\n/).filter(ln => ln.length > 0);
      csv = rows.map(row => {
        const cells = row.split('\t');
        return cells.map(c => {
          const needsQuote = /[",\n]/.test(c);
          const escaped = c.replace(/"/g, '""');
          return needsQuote ? `"${escaped}"` : escaped;
        }).join(',');
      }).join('\n');
    }
    // Build a synthetic File so the existing endpoint sees a CSV upload
    const blob = new Blob([csv], { type: 'text/csv' });
    const pastedFile = new File([blob], `pasted_${Date.now()}.csv`, { type: 'text/csv' });
    setFile(pastedFile);
    setShowPaste(false);
    setPasteText('');
    toast.info('Pasted content captured — click "Upload to Staging" to proceed.', { duration: 4000 });
  };

  // Collision-modal state — surfaced when the backend returns 409
  // duplicate_filename. Sub-admins never see this (they can't upload).
  const [collision, setCollision] = useState(null); // { message, existing_row_count, existing_uploaded_at, attempted_filename }
  const [renameValue, setRenameValue] = useState('');
  const [renameError, setRenameError] = useState('');

  const handleUpload = async (renameOverride = null) => {
    // Defensive: `handleUpload` is called with an explicit string from
    // `submitRename`, but if any onClick handler forgets to wrap it in
    // an arrow function (`onClick={handleUpload}` instead of
    // `onClick={() => handleUpload()}`) React passes the click event as
    // the first argument. Stringifying that produces "[object Object]",
    // which is what caused prod uploads to be named "[object Object].csv".
    // Only accept a non-empty string; anything else is treated as null.
    const rename = typeof renameOverride === 'string' && renameOverride.trim()
      ? renameOverride.trim()
      : null;

    if (!file) {
      toast.error('Please select a file first');
      return;
    }

    if (!selectedGrade || !selectedMMD || !selectedFunction) {
      toast.error('Please select Grade, MMD Location, and Function first');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const qs = new URLSearchParams({
        grade: selectedGrade,
        mmd_location: selectedMMD,
        function_id: selectedFunction,
      });
      if (rename) qs.append('rename', rename);

      const response = await axios.post(
        `${API}/admin/bulk-import/upload?${qs.toString()}`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );

      toast.success(`Successfully imported ${response.data.question_count} questions to staging!`);
      if (response.data?.legacy_xlsx_converted) {
        toast.info('Legacy .xlsx detected — converted to CSV in memory. Consider switching to the new CSV template next time.', { duration: 6000 });
      }
      const warnCount = response.data?.surveyor_warnings?.length || 0;
      const dupCount = response.data?.duplicates_in_csv?.length || 0;
      const conflictCount = response.data?.surveyor_conflicts?.length || 0;
      const semDupCount = response.data?.semantic_duplicates_in_csv?.length || 0;
      if (warnCount || dupCount || conflictCount || semDupCount) {
        const parts = [];
        if (warnCount) parts.push(`${warnCount} surveyor name(s) need review`);
        if (conflictCount) parts.push(`${conflictCount} surveyor conflict(s)`);
        if (dupCount) parts.push(`${dupCount} exact duplicate(s)`);
        if (semDupCount) parts.push(`${semDupCount} near-duplicate pair(s) (≥80% similar)`);
        toast.warning(`Heads-up: ${parts.join(', ')}. Open the staging review (try Bulk Similarity) to resolve.`, { duration: 8000 });
      }
      setFile(null);
      setCollision(null);
      setRenameValue('');
      setRenameError('');

      // Reset file input
      const fileInput = document.getElementById('file-upload');
      if (fileInput) fileInput.value = '';

      // Refresh data
      fetchImportHistory();
      fetchStagingCount();

      // Offer to go to review page
      if (window.confirm(`${response.data.question_count} questions imported. Go to Review page now?`)) {
        navigate('/admin/bulk-import-review');
      }

    } catch (error) {
      const detail = error.response?.data?.detail;
      // Structured 409 → open rename modal instead of a generic toast.
      if (error.response?.status === 409 && detail?.code === 'duplicate_filename') {
        setCollision(detail);
        setRenameValue('');
        setRenameError('');
      } else {
        console.error('Failed to upload:', error);
        const msg = typeof detail === 'string' ? detail : (detail?.message || 'Failed to upload file');
        toast.error(msg);
      }
    } finally {
      setUploading(false);
    }
  };

  const submitRename = () => {
    const trimmed = (renameValue || '').trim();
    if (!trimmed) {
      setRenameError('Please type a new filename.');
      return;
    }
    if (trimmed === collision?.attempted_filename) {
      setRenameError('Pick a different name — this one still collides.');
      return;
    }
    if (trimmed.length > 200) {
      setRenameError('Filename must be 200 characters or fewer.');
      return;
    }
    if (trimmed.includes('/') || trimmed.includes('\\')) {
      setRenameError('Filename must not contain slashes.');
      return;
    }
    setRenameError('');
    handleUpload(trimmed);
  };

  // Cancel = walk away from this upload attempt. The current file was
  // NEVER staged (the backend returned 409 BEFORE parsing), so there is
  // NOTHING to delete on either side. Just close the modal, clear form
  // state, and let the admin decide what to do next. The previous batch
  // that caused the collision remains untouched — as it should.
  const cancelUpload = useCallback(() => {
    setCollision(null);
    setRenameValue('');
    setRenameError('');
    setFile(null);
    const fileInput = document.getElementById('file-upload');
    if (fileInput) fileInput.value = '';
    toast.info('Upload cancelled. The previous batch was left as-is.');
  }, []);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSelectedFunctionName = () => {
    const func = functions.find(f => f.id === selectedFunction);
    if (!func) return '';
    const suffix = func.function_suffix && func.function_suffix !== 'None' && func.function_suffix !== 'null' ? func.function_suffix : '';
    return `F${func.function_number}${suffix} - ${func.name}`;
  };

  return (
    <Layout>
      <div className="container mx-auto py-8 px-4 max-w-6xl">
        <Button
          variant="ghost"
          onClick={() => navigate('/admin/content')}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Content Management
        </Button>
        
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Bulk Import Questions</h1>
          <p className="text-gray-600 mt-1">
            Download template, fill questions, and upload for review
          </p>
        </div>

        {/* Staging Alert */}
        {stagingCount > 0 && (
          <Alert className="mb-6 border-blue-200 bg-blue-50">
            <Info className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-800">
              You have <strong>{stagingCount} questions</strong> in staging waiting for review.{' '}
              <Button
                variant="link"
                className="px-1 text-blue-600 font-semibold"
                onClick={() => navigate('/admin/bulk-import-review')}
              >
                Go to Review Page →
              </Button>
            </AlertDescription>
          </Alert>
        )}

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Step 1: Download Template */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Download className="h-5 w-5 text-blue-600" />
                Step 1: Download Template
              </CardTitle>
              <CardDescription>
                Select criteria and download a CSV template — includes a footer with the surveyor list for this scope
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Grade <span className="text-red-500">*</span></Label>
                <Select value={selectedGrade} onValueChange={setSelectedGrade}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select grade" />
                  </SelectTrigger>
                  <SelectContent>
                    {grades.map((grade) => (
                      <SelectItem key={grade} value={grade}>{grade}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>MMD Location <span className="text-red-500">*</span></Label>
                <Select value={selectedMMD} onValueChange={setSelectedMMD}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select MMD" />
                  </SelectTrigger>
                  <SelectContent>
                    {mmdLocations.map((mmd) => (
                      <SelectItem key={mmd} value={mmd}>{mmd}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Function <span className="text-red-500">*</span></Label>
                <Select 
                  value={selectedFunction} 
                  onValueChange={setSelectedFunction}
                  disabled={!selectedGrade}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={selectedGrade ? "Select function" : "Select grade first"} />
                  </SelectTrigger>
                  <SelectContent>
                    {functions.length > 0 ? (
                      functions.map((func) => {
                        const suffix = func.function_suffix && func.function_suffix !== 'None' && func.function_suffix !== 'null' ? func.function_suffix : '';
                        return (
                          <SelectItem key={func.id} value={func.id}>
                            F{func.function_number}{suffix} - {func.name}
                          </SelectItem>
                        );
                      })
                    ) : (
                      <div className="px-2 py-1 text-sm text-gray-500">
                        {selectedGrade ? 'No functions available' : 'Select a grade first'}
                      </div>
                    )}
                  </SelectContent>
                </Select>
              </div>

              <Button
                className="w-full"
                onClick={handleDownloadTemplate}
                disabled={downloadingTemplate || !selectedGrade || !selectedMMD || !selectedFunction}
              >
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                {downloadingTemplate ? 'Generating...' : 'Download CSV Template'}
              </Button>

              {selectedGrade && selectedMMD && selectedFunction && (
                <p className="text-xs text-gray-500 text-center">
                  Template will include surveyors for {selectedGrade} / {selectedMMD}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Step 2: Upload File — disabled with overlay for sub-admins.
              Sub-admins can still see the batches list below; only the
              upload panel itself is gated. */}
          <Card className="relative">
            {isSubAdmin && (
              <div
                className="absolute inset-0 z-20 bg-white/85 backdrop-blur-[1px] rounded-lg flex flex-col items-center justify-center px-8 text-center border-2 border-dashed border-gray-300"
                data-testid="upload-panel-subadmin-lock"
              >
                <Lock className="h-10 w-10 text-gray-400 mb-3" />
                <div className="text-lg font-semibold text-gray-800 mb-1">
                  Uploads are limited to admins
                </div>
                <div className="text-sm text-gray-600 max-w-md">
                  Please send your filled CSV to an admin to add it to the platform.
                  Once staged, you&apos;ll be able to review it here.
                </div>
                <div className="mt-3 text-xs text-gray-500">
                  Tip: use <strong>Download Template</strong> above to grab the correct
                  CSV format before handing off.
                </div>
              </div>
            )}
            <div aria-hidden={isSubAdmin} className={isSubAdmin ? 'pointer-events-none select-none' : ''}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="h-5 w-5 text-green-600" />
                Step 2: Upload Filled Template
              </CardTitle>
              <CardDescription>
                Upload your filled CSV (.xlsx still accepted as legacy — auto-converted). Questions land in staging for review.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert className="border-amber-200 bg-amber-50">
                <AlertCircle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-800 text-sm">
                  <strong>Before uploading:</strong> Make sure the same Grade, MMD, and Function are selected as when you downloaded the template.
                </AlertDescription>
              </Alert>

              <div>
                <Label>Selected Criteria</Label>
                <div className="mt-1 flex flex-wrap gap-2">
                  {selectedGrade && <Badge variant="secondary">{selectedGrade}</Badge>}
                  {selectedMMD && <Badge variant="secondary">{selectedMMD}</Badge>}
                  {selectedFunction && <Badge variant="secondary">{getSelectedFunctionName()}</Badge>}
                  {(!selectedGrade || !selectedMMD || !selectedFunction) && (
                    <span className="text-sm text-gray-500">Select all criteria above</span>
                  )}
                </div>
              </div>

              <div>
                <Label>CSV file (.xlsx legacy ok)</Label>
                <input
                  id="file-upload"
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleFileChange}
                  className="mt-1 block w-full text-sm text-gray-500
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-md file:border-0
                    file:text-sm file:font-semibold
                    file:bg-blue-50 file:text-blue-700
                    hover:file:bg-blue-100
                    cursor-pointer"
                />
                {file && (
                  <p className="mt-1 text-sm text-gray-600">
                    Selected: {file.name}
                  </p>
                )}
              </div>

              {/* Excel-paste alternative — skips the save-as-CSV step entirely */}
              <div className="border-t pt-3">
                {!showPaste ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowPaste(true)}
                    className="text-xs"
                    data-testid="open-paste-area-btn"
                  >
                    <ClipboardPaste className="h-3 w-3 mr-1" />
                    Or paste directly from Excel / Sheets
                  </Button>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm flex items-center gap-2">
                        <ClipboardPaste className="h-4 w-4 text-emerald-600" />
                        Paste from Excel / Sheets
                      </Label>
                      <Button variant="ghost" size="sm" className="h-6 text-xs"
                        onClick={() => { setShowPaste(false); setPasteText(''); }}>
                        Cancel
                      </Button>
                    </div>
                    <Textarea
                      value={pasteText}
                      onChange={(e) => setPasteText(e.target.value)}
                      placeholder={`Paste rows here (copy from Excel/Sheets includes headers).
Example:
Question\tAsked by Internal\tInternal Surveyor\tExternal Surveyor\tMonth\tYear\tAsked\tDecider\tKeywords
What is MARPOL?\t0\t\tCapt. Sharma\tJan\t2025\t1\t0\tmarpol`}
                      className="min-h-[140px] font-mono text-xs"
                      data-testid="paste-textarea"
                    />
                    <p className="text-[11px] text-gray-500">
                      Tab-separated (Excel default) and comma-separated both work. The parser sniffs the delimiter from the first line.
                    </p>
                    <Button
                      size="sm"
                      onClick={handlePasteUpload}
                      className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid="paste-convert-btn"
                    >
                      <ClipboardPaste className="h-3 w-3 mr-1" />
                      Convert &amp; stage as file
                    </Button>
                  </div>
                )}
              </div>

              <Button
                className="w-full"
                onClick={() => handleUpload()}
                disabled={uploading || !file || !selectedGrade || !selectedMMD || !selectedFunction}
              >
                {uploading ? (
                  <>Uploading...</>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Upload to Staging
                  </>
                )}
              </Button>
            </CardContent>
            </div>
          </Card>
        </div>

        {/* Import History */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-gray-600" />
              Import History
            </CardTitle>
            <CardDescription>
              Recent file imports (file is deleted after parsing, only metadata is stored)
            </CardDescription>
          </CardHeader>
          <CardContent>
            {importHistory.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No import history yet</p>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Filename</TableHead>
                      <TableHead>Grade / MMD</TableHead>
                      <TableHead>Function</TableHead>
                      <TableHead>Questions</TableHead>
                      <TableHead>Uploaded By</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {importHistory
                      .slice(
                        (historyCurrentPage - 1) * historyItemsPerPage,
                        historyCurrentPage * historyItemsPerPage
                      )
                      .map((item) => (
                      <TableRow key={item.id}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <FileSpreadsheet className="h-4 w-4 text-green-600" />
                            {item.filename}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{item.grade}</Badge>
                          <Badge variant="outline" className="ml-1">{item.mmd_location}</Badge>
                        </TableCell>
                        <TableCell className="text-sm">{item.function_name || '-'}</TableCell>
                        <TableCell>
                          <Badge>{item.question_count}</Badge>
                        </TableCell>
                        <TableCell className="text-sm">{item.uploaded_by_name || '-'}</TableCell>
                        <TableCell className="text-sm text-gray-500">
                          {formatDate(item.uploaded_at)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                
                {/* Pagination */}
                <Pagination
                  currentPage={historyCurrentPage}
                  totalItems={importHistory.length}
                  itemsPerPage={historyItemsPerPage}
                  onPageChange={(page) => setHistoryCurrentPage(page)}
                  onItemsPerPageChange={(perPage) => {
                    setHistoryItemsPerPage(perPage);
                    setHistoryCurrentPage(1); // Reset to first page when changing items per page
                  }}
                />
              </>
            )}
          </CardContent>
        </Card>

        {/* Instructions */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Info className="h-5 w-5 text-blue-600" />
              How It Works
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="list-decimal list-inside space-y-2 text-gray-700">
              <li><strong>Download Template:</strong> Select Grade, MMD, and Function, then download the CSV template. It includes a footer block listing the available surveyors so you can copy-paste names without leaving the file.</li>
              <li><strong>Fill Questions:</strong> Add your questions in the CSV. The 9 columns plus optional Date column are documented in the footer of the downloaded template.</li>
              <li><strong>Upload:</strong> Upload the filled file. Questions will go to a staging area for review.</li>
              <li><strong>Review:</strong> Go to the Review page to check for duplicates, edit if needed, and approve questions.</li>
            </ol>

            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2">CSV Columns:</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• <strong>Question:</strong> The question text (required)</li>
                <li>• <strong>Asked by Internal:</strong> 1 = internal surveyor, 0 (or blank) = external</li>
                <li>• <strong>Internal Surveyor:</strong> name; used when Asked by Internal = 1 (fuzzy-matched)</li>
                <li>• <strong>External Surveyor:</strong> name; used when Asked by Internal = 0 (fuzzy-matched)</li>
                <li>• <strong>Month:</strong> When the question was asked (defaults to current month if empty)</li>
                <li>• <strong>Year:</strong> Year of the question (defaults to current year if empty)</li>
                <li>• <strong>Asked:</strong> Counter — number of times this question was asked (default 1)</li>
                <li>• <strong>Decider:</strong> 1/yes/true marks the question as a decider</li>
                <li>• <strong>Keywords:</strong> free-form tag(s) for staging-review filtering; use &quot;;&quot; to combine: <code>engine;lubrication</code></li>
              </ul>
              <p className="text-xs text-gray-500 mt-2">
                <strong>Tip:</strong> instead of separate Month + Year, you can add a single <code>Date</code> column like <code>Jun 2025</code> or <code>2025-06</code> and the parser will extract month + year automatically.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Duplicate-filename collision modal — surfaces when the backend
          returns 409 duplicate_filename. Two exits only:
          - "Rename & upload" retries with a fresh filename (keeps both
            batches — old rows stay in staging, new file gets staged under
            the renamed value).
          - "Cancel" walks away from this upload attempt entirely. The
            current file was NEVER staged (backend returned 409 BEFORE
            parsing), so nothing needs to be deleted on either side. The
            previous batch remains untouched.
          No passive-close path — Esc / backdrop click / the X-icon are
          all suppressed (the X is hidden via hideCloseButton) so the
          admin must pick one of the two CTAs. */}
      <Dialog
        open={!!collision}
        onOpenChange={(open) => {
          // Suppress passive close. The two footer buttons are the only
          // way out.
          if (!open && !uploading) {
            // no-op — force explicit CTA
          }
        }}
      >
        <DialogContent
          data-testid="upload-collision-dialog"
          hideCloseButton
          onEscapeKeyDown={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>Filename already in staging</DialogTitle>
            <DialogDescription>
              {collision?.message || 'A file with this name is already un-triaged.'}
              {collision?.existing_uploaded_at && (
                <>
                  {' '}
                  <span className="block mt-1 text-xs text-gray-500">
                    Previous upload: {formatDate(collision.existing_uploaded_at)}
                  </span>
                </>
              )}
              <span className="block mt-2 text-xs text-gray-700">
                <strong>Rename &amp; upload</strong> stages this file under a fresh name;
                the previous batch stays in place.{' '}
                <strong>Cancel</strong> discards the file you just picked and closes this
                dialog — nothing is staged, and the previous batch is left as-is.
              </span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="rename-input" className="text-sm">
              Rename this upload
            </Label>
            <Input
              id="rename-input"
              data-testid="upload-collision-rename-input"
              placeholder={`e.g. ${(collision?.attempted_filename || 'upload.csv').replace(/\.csv$/i, '')}_v2`}
              value={renameValue}
              onChange={(e) => {
                setRenameValue(e.target.value);
                if (renameError) setRenameError('');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') submitRename();
              }}
              autoFocus
              disabled={uploading}
            />
            {renameError ? (
              <p className="text-xs text-red-600" data-testid="upload-collision-rename-error">
                {renameError}
              </p>
            ) : (
              <p className="text-xs text-gray-500">
                We'll append <code>.csv</code> if you leave it off. The renamed value is stored
                as this upload's filename — nothing on disk is touched.
              </p>
            )}
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={cancelUpload}
              disabled={uploading}
              data-testid="upload-collision-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={submitRename}
              disabled={uploading}
              data-testid="upload-collision-rename-submit"
            >
              {uploading ? 'Uploading…' : 'Rename & upload'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default BulkImportQuestionsPage;
